/**
 * Discover Me 2.0, 7-dimension structured assessment.
 *
 * Design notes (from user feedback §13):
 *   • Learning "PREFERENCES", never "learning style". The screen carries a
 *     non-prescriptive disclaimer so no student is told what to learn based on
 *     these responses. They just help us describe experiences that might feel
 *     comfortable.
 *   • AI Level 0, scoring is deterministic on the backend. This page NEVER
 *     calls an LLM. AI is only invoked (optionally, later) to explain results.
 *   • Reuse existing profile data, never re-ask what we already know.
 */
import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { AppLayout } from "../components/Layout";
import { toast } from "sonner";
import {
  Compass, Heart, Building2, Wrench, Sparkles, Target, GraduationCap,
  BookOpen, ChevronRight, ChevronLeft, CheckCircle2, Loader2, RefreshCw,
  Info, ShieldCheck, ArrowLeft,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// The order students walk through the assessment.
const DIMENSION_ORDER = [
  { key: "interests",            icon: Compass,      accent: "sky",      label: "Interests" },
  { key: "strengths",            icon: Sparkles,     accent: "amber",    label: "Strengths" },
  { key: "values",               icon: Heart,        accent: "rose",     label: "Values" },
  { key: "work_environment",     icon: Building2,    accent: "emerald",  label: "Work Environment" },
  { key: "work_style",           icon: Wrench,       accent: "violet",   label: "Work Style" },
  { key: "lifestyle",            icon: Target,       accent: "cyan",     label: "Lifestyle" },
  { key: "learning_preferences", icon: BookOpen,     accent: "fuchsia",  label: "Learning Preferences" },
];

const ACCENT = {
  sky:     "from-sky-500 to-cyan-500 text-sky-200",
  amber:   "from-amber-500 to-orange-500 text-amber-200",
  rose:    "from-rose-500 to-pink-500 text-rose-200",
  emerald: "from-emerald-500 to-green-500 text-emerald-200",
  violet:  "from-violet-500 to-purple-500 text-violet-200",
  cyan:    "from-cyan-500 to-teal-500 text-cyan-200",
  fuchsia: "from-fuchsia-500 to-pink-500 text-fuchsia-200",
};

// ═══════════════════════════════════════════════════════════════════════════
function LikertRow({ q, value, onChange }) {
  const scale = [1, 2, 3, 4, 5];
  const label = { 1: "Not me", 5: "Definitely me" };
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-4" data-testid={`q-${q.id}`}>
      <div className="text-slate-100 font-medium mb-3">{q.text}</div>
      <div className="flex items-center justify-between gap-1">
        <span className="text-[10px] text-slate-300 uppercase w-16 text-right">{label[1]}</span>
        {scale.map((n) => (
          <button key={n}
                  onClick={() => onChange(n)}
                  data-testid={`q-${q.id}-${n}`}
                  className={`w-9 h-9 rounded-full font-bold text-sm transition-all ${
                    value === n
                      ? "bg-cyan-500 text-white ring-2 ring-cyan-300 scale-110"
                      : "bg-slate-800 text-slate-300 hover:bg-slate-700"
                  }`}>
            {n}
          </button>
        ))}
        <span className="text-[10px] text-slate-300 uppercase w-16">{label[5]}</span>
      </div>
    </div>
  );
}

function ChoiceRow({ q, value, onChange }) {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-4" data-testid={`q-${q.id}`}>
      <div className="text-slate-100 font-medium mb-3">{q.text}</div>
      <div className="flex flex-wrap gap-2">
        {q.options.map((opt) => (
          <button key={opt} onClick={() => onChange(opt)}
                  data-testid={`q-${q.id}-${opt}`}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                    value === opt
                      ? "bg-cyan-500 text-white ring-1 ring-cyan-300"
                      : "bg-slate-800 text-slate-300 hover:bg-slate-700 ring-1 ring-slate-700"
                  }`}>
            {opt}
          </button>
        ))}
      </div>
    </div>
  );
}

function NumberRow({ q, value, onChange }) {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-4" data-testid={`q-${q.id}`}>
      <div className="text-slate-100 font-medium mb-3">{q.text}</div>
      <div className="flex items-center gap-2">
        <input type="number" value={value || ""}
               onChange={(e) => onChange(e.target.value ? Number(e.target.value) : "")}
               className="w-40 px-3 py-2 rounded-lg bg-slate-800 text-white ring-1 ring-slate-700 focus:ring-cyan-400 focus:outline-none" />
        {q.unit && <span className="text-slate-300 text-sm">{q.unit}</span>}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
function DimensionForm({ dim, dimData, responses, onChange }) {
  return (
    <div className="space-y-3">
      {dim.description && (
        <div className="text-sm text-slate-300 -mt-2">{dim.description}</div>
      )}
      {/* Learning-preferences: non-prescriptive disclaimer */}
      {dimData.key === "learning_preferences" && (
        <div className="rounded-xl bg-fuchsia-500/10 ring-1 ring-fuchsia-400/30 px-4 py-3 flex items-start gap-2"
             data-testid="learning-pref-disclaimer">
          <Info className="w-4 h-4 text-fuchsia-300 mt-0.5 shrink-0" />
          <div className="text-xs text-fuchsia-100 leading-relaxed">
            <strong>These are preferences, not prescriptions.</strong> Research shows
            students learn effectively in many ways. We use these answers to describe
            experiences that might feel comfortable, never to tell you which classes
            are "right" or "wrong" for you.
          </div>
        </div>
      )}
      {dim.questions.map((q) => {
        const kind = q.kind || "likert";
        if (kind === "choice") {
          return <ChoiceRow key={q.id} q={q} value={responses[q.id]}
                            onChange={(v) => onChange(q.id, v)} />;
        }
        if (kind === "number") {
          return <NumberRow key={q.id} q={q} value={responses[q.id]}
                            onChange={(v) => onChange(q.id, v)} />;
        }
        return <LikertRow key={q.id} q={q} value={responses[q.id]}
                          onChange={(v) => onChange(q.id, v)} />;
      })}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
function StrengthsForm({ catalog, initial, onSave, busy }) {
  const [skills, setSkills] = useState(new Set(initial?.skills || []));
  const [subjects, setSubjects] = useState(new Set(initial?.subjects || []));
  const [demonstrated, setDemonstrated] = useState(new Set(initial?.demonstrated || []));

  const toggle = (setFn, set, val) => {
    const next = new Set(set);
    if (next.has(val)) next.delete(val); else next.add(val);
    setFn(next);
  };

  return (
    <div className="space-y-5">
      <StrengthGroup title="Skills you'd say you have"
                     items={catalog.skills}
                     set={skills}
                     onToggle={(v) => toggle(setSkills, skills, v)}
                     testid="skills" />
      <StrengthGroup title="Subjects you're strongest in"
                     items={catalog.subjects}
                     set={subjects}
                     onToggle={(v) => toggle(setSubjects, subjects, v)}
                     testid="subjects" />
      <StrengthGroup title="Things you've actually done"
                     items={catalog.demonstrated}
                     set={demonstrated}
                     onToggle={(v) => toggle(setDemonstrated, demonstrated, v)}
                     testid="demonstrated" />
      <button onClick={() => onSave({
                skills: [...skills], subjects: [...subjects], demonstrated: [...demonstrated],
              })}
              disabled={busy}
              data-testid="save-strengths-btn"
              className="w-full px-4 py-2.5 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white font-semibold">
        {busy ? "Saving..." : "Save strengths"}
      </button>
    </div>
  );
}

function StrengthGroup({ title, items, set, onToggle, testid }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-slate-300 font-semibold mb-2">
        {title} ({set.size})
      </div>
      <div className="flex flex-wrap gap-1.5">
        {items.map((s) => {
          const active = set.has(s);
          return (
            <button key={s} onClick={() => onToggle(s)}
                    data-testid={`${testid}-${s.replace(/\s+/g,'-').toLowerCase()}`}
                    className={`text-xs px-2.5 py-1 rounded-full font-medium transition-all ${
                      active
                        ? "bg-amber-500/25 text-amber-100 ring-1 ring-amber-400/60"
                        : "bg-slate-800 text-slate-300 hover:bg-slate-700 ring-1 ring-slate-700"
                    }`}>
              {s}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
function ResultsPanel({ results, onRetake }) {
  const s = results.scores || {};
  return (
    <div className="space-y-5" data-testid="discover-results">
      <div className="rounded-2xl border border-cyan-500/40 bg-gradient-to-br from-cyan-500/10 to-fuchsia-500/10 p-5">
        <div className="flex items-center gap-2 text-cyan-300 font-semibold text-xs uppercase tracking-wider">
          <Sparkles className="w-4 h-4" /> Your Discover Me profile
        </div>
        {results.holland_code && (
          <div className="mt-2 text-3xl font-black text-white">
            Holland Code: <span className="text-cyan-300">{results.holland_code}</span>
          </div>
        )}
        {results.summary_lines && results.summary_lines.length > 0 && (
          <ul className="mt-3 space-y-1.5">
            {results.summary_lines.map((line, i) => (
              <li key={i} className="text-slate-100 flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-300 mt-0.5 shrink-0" />
                <span className="leading-snug">{line}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {Object.entries(s).map(([dimKey, scored]) => {
        if (!scored.top_3 || scored.top_3.length === 0) return null;
        const dimMeta = DIMENSION_ORDER.find(d => d.key === dimKey);
        if (!dimMeta) return null;
        const Icon = dimMeta.icon;
        return (
          <div key={dimKey}
               className="rounded-xl border border-slate-800 bg-slate-900/50 p-4">
            <div className="flex items-center gap-2 mb-3">
              <Icon className={`w-4 h-4 ${ACCENT[dimMeta.accent].split(" ").pop()}`} />
              <div className="font-semibold text-white">{dimMeta.label}</div>
            </div>
            <div className="space-y-2">
              {scored.top_3.map((t, idx) => (
                <div key={t.key} className="flex items-center gap-3">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold
                                     ${idx === 0 ? "bg-cyan-500 text-white"
                                                 : "bg-slate-800 text-slate-300"}`}>
                    {idx + 1}
                  </span>
                  <span className="text-slate-100 flex-1">{t.label}</span>
                  <span className="text-sm font-mono text-slate-300 w-10 text-right">{t.score}</span>
                  <div className="w-32 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                    <div className={`h-full bg-gradient-to-r ${ACCENT[dimMeta.accent].split(" text-")[0]}`}
                         style={{ width: `${t.score}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {results.strengths && Object.keys(results.strengths).length > 0 && (
        <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-4">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-amber-300" />
            <div className="font-semibold text-white">Strengths</div>
          </div>
          <StrengthPills label="Skills"       items={results.strengths.skills || []} />
          <StrengthPills label="Subjects"     items={results.strengths.subjects || []} />
          <StrengthPills label="Demonstrated" items={results.strengths.demonstrated || []} />
        </div>
      )}

      <div className="rounded-xl bg-slate-800/50 ring-1 ring-slate-700 p-3 text-xs text-slate-300 flex items-center gap-2"
           data-testid="ai-transparency-badge">
        <ShieldCheck className="w-3.5 h-3.5 text-emerald-300 shrink-0" />
        Every score above was calculated deterministically from your answers.
        No AI was used to interpret or invent your profile.
      </div>

      <button onClick={onRetake}
              data-testid="retake-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 font-semibold text-sm ring-1 ring-slate-700">
        <RefreshCw className="w-4 h-4" /> Start Over
      </button>
    </div>
  );
}

function StrengthPills({ label, items }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="mt-2">
      <div className="text-[10px] uppercase tracking-wide text-slate-300 font-semibold">{label}</div>
      <div className="flex flex-wrap gap-1 mt-1">
        {items.map((i) => (
          <span key={i} className="text-xs px-2 py-0.5 rounded bg-amber-500/15 text-amber-100 ring-1 ring-amber-400/30">
            {i}
          </span>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
export default function DiscoverMePage() {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [questionnaire, setQuestionnaire] = useState(null);
  const [results, setResults]             = useState(null);
  const [step, setStep]                   = useState(0);         // index into DIMENSION_ORDER
  const [view, setView]                   = useState("intro");   // intro | wizard | results
  const [responses, setResponses]         = useState({});
  const [busy, setBusy]                   = useState(false);
  const [loading, setLoading]             = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [q, r] = await Promise.all([
          axios.get(`${API}/discover-me/questionnaire`, getAuthHeader()),
          axios.get(`${API}/discover-me/results`,       getAuthHeader()),
        ]);
        setQuestionnaire(q.data);
        setResults(r.data);
        // If they've completed anything already, show results with option to continue
        if ((r.data.completed_dimensions || []).length > 0) {
          setView("results");
        }
        // Preload prior responses so we never re-ask
        setResponses(q.data.existing?.responses || {});
      } catch (e) {
        toast.error("Couldn't load Discover Me");
      } finally { setLoading(false); }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentDim = DIMENSION_ORDER[step];
  const dimData    = questionnaire?.dimensions?.[currentDim?.key];

  const setResponse = (dimKey, questionId, value) => {
    setResponses((prev) => ({
      ...prev,
      [dimKey]: { ...(prev[dimKey] || {}), [questionId]: value },
    }));
  };

  const saveCurrentDimension = async () => {
    if (currentDim.key === "strengths") return; // strengths uses its own save button
    setBusy(true);
    try {
      await axios.post(`${API}/discover-me/dimension`,
                       { dimension: currentDim.key, responses: responses[currentDim.key] || {} },
                       getAuthHeader());
      toast.success(`${currentDim.label} saved`);
      const r = await axios.get(`${API}/discover-me/results`, getAuthHeader());
      setResults(r.data);
    } catch (e) {
      toast.error("Save failed", { description: e.response?.data?.detail });
    } finally { setBusy(false); }
  };

  const saveStrengths = async (payload) => {
    setBusy(true);
    try {
      await axios.post(`${API}/discover-me/strengths`, payload, getAuthHeader());
      toast.success("Strengths saved");
      const r = await axios.get(`${API}/discover-me/results`, getAuthHeader());
      setResults(r.data);
    } catch (e) {
      toast.error("Save failed");
    } finally { setBusy(false); }
  };

  const next = async () => {
    await saveCurrentDimension();
    if (step >= DIMENSION_ORDER.length - 1) {
      setView("results");
    } else {
      setStep(step + 1);
    }
  };

  const skip = () => {
    if (step >= DIMENSION_ORDER.length - 1) setView("results");
    else setStep(step + 1);
  };

  const startFresh = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/discover-me/version`, {}, getAuthHeader());
      setResponses({});
      setStep(0);
      setView("wizard");
      const q = await axios.get(`${API}/discover-me/questionnaire`, getAuthHeader());
      setQuestionnaire(q.data);
      toast.success("New assessment started, your old profile is saved");
    } catch (e) { toast.error("Couldn't start retake"); }
    finally { setBusy(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-slate-300" />
      </div>
    );
  }

  const completed = new Set(results?.completed_dimensions || []);
  const totalSteps = DIMENSION_ORDER.length;
  const progress = Math.round((completed.size / totalSteps) * 100);

  return (
    <AppLayout>
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950"
         data-testid="discover-me-page">
      <div className="max-w-3xl mx-auto px-4 md:px-6 py-8 space-y-6">

        {/* Back to Assessment */}
        <button onClick={() => navigate("/app/assessment")}
                data-testid="back-to-assessment"
                className="inline-flex items-center gap-2 text-slate-300 hover:text-slate-200 text-sm">
          <ArrowLeft className="w-4 h-4" /> Back to Assessment
        </button>

        {/* Header */}
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-white">Discover Me</h1>
        </div>

        {view === "intro" && (
          <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {DIMENSION_ORDER.map((d) => {
                const Icon = d.icon;
                const done = completed.has(d.key);
                return (
                  <div key={d.key}
                       className={`rounded-lg p-3 text-center ring-1 ${
                         done
                           ? "bg-emerald-500/10 ring-emerald-400/40"
                           : "bg-slate-800/50 ring-slate-700"
                       }`}>
                    <Icon className={`w-5 h-5 mx-auto ${done ? "text-emerald-300" : "text-slate-300"}`} />
                    <div className="text-[11px] mt-1 text-slate-200">{d.label}</div>
                    {done && <div className="text-[9px] text-emerald-300 mt-0.5">✓ Done</div>}
                  </div>
                );
              })}
            </div>
            <button onClick={() => setView("wizard")}
                    data-testid="begin-btn"
                    className="w-full px-4 py-3 rounded-xl bg-gradient-to-r from-cyan-500 to-fuchsia-500 hover:from-cyan-400 hover:to-fuchsia-400 text-white font-semibold">
              {completed.size > 0 ? "Continue where I left off" : "Get Started"}
            </button>
          </div>
        )}

        {view === "wizard" && questionnaire && (
          <>
            {/* Progress + step nav */}
            <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-wide text-slate-300 font-semibold">
                  Step {step + 1} of {totalSteps}
                </div>
                <div className="text-xs text-slate-300">{progress}% complete</div>
              </div>
              <div className="w-full h-1.5 bg-slate-800 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-cyan-500 to-fuchsia-500 transition-all"
                     style={{ width: `${progress}%` }} />
              </div>
              <div className="mt-3 flex items-center gap-2">
                {(() => { const Icon = currentDim.icon; return (
                  <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${ACCENT[currentDim.accent].split(" text-")[0]} flex items-center justify-center`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                );})()}
                <div>
                  <div className="text-white font-bold">{currentDim.label}</div>
                  {dimData?.description && (
                    <div className="text-xs text-slate-300">{dimData.description.split(".")[0]}.</div>
                  )}
                </div>
              </div>
            </div>

            {/* Dimension form */}
            {currentDim.key === "strengths" ? (
              <StrengthsForm catalog={questionnaire.strengths_catalog}
                             initial={questionnaire.existing?.strengths}
                             onSave={saveStrengths}
                             busy={busy} />
            ) : (
              <DimensionForm dim={dimData}
                             dimData={currentDim}
                             responses={responses[currentDim.key] || {}}
                             onChange={(qid, v) => setResponse(currentDim.key, qid, v)} />
            )}

            {/* Nav buttons */}
            <div className="flex items-center justify-between">
              <button onClick={() => setStep(Math.max(0, step - 1))}
                      disabled={step === 0}
                      data-testid="back-btn"
                      className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-slate-300 hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed">
                <ChevronLeft className="w-4 h-4" /> Back
              </button>
              <div className="flex items-center gap-2">
                <button onClick={skip}
                        data-testid="skip-btn"
                        className="text-sm text-slate-300 hover:text-slate-200 px-3 py-2">
                  Skip for now
                </button>
                {currentDim.key !== "strengths" && (
                  <button onClick={next}
                          disabled={busy}
                          data-testid="next-btn"
                          className="inline-flex items-center gap-1 px-4 py-2 rounded-lg bg-gradient-to-r from-cyan-500 to-fuchsia-500 hover:from-cyan-400 hover:to-fuchsia-400 text-white font-semibold disabled:opacity-50">
                    {step >= totalSteps - 1 ? "Finish" : "Next"} <ChevronRight className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          </>
        )}

        {view === "results" && results && (
          <>
            <ResultsPanel results={results} onRetake={startFresh} />
            {/* Encourage the student to finish incomplete dimensions */}
            {completed.size < totalSteps && (
              <button onClick={() => {
                        // jump to first incomplete dimension
                        const idx = DIMENSION_ORDER.findIndex(d => !completed.has(d.key));
                        setStep(Math.max(0, idx));
                        setView("wizard");
                      }}
                      data-testid="continue-assessment-btn"
                      className="w-full px-4 py-3 rounded-xl bg-gradient-to-r from-cyan-500 to-fuchsia-500 hover:from-cyan-400 hover:to-fuchsia-400 text-white font-semibold">
                Continue assessment ({totalSteps - completed.size} left)
              </button>
            )}
          </>
        )}
      </div>
    </div>
    </AppLayout>
  );
}
